import type { FC } from "react"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Stack,
  Text,
  Link,
  useBoolean,
  Flex,
  Icon,
  useDisclosure,
} from "@chakra-ui/react"
import type { FieldProps } from "formik"
import { Field, useFormikContext } from "formik"
import { isAddress } from "@ethersproject/address"
import { formatUnits } from "ethers/lib/utils.js"
import BigNumber from "bignumber.js"

import TallyFeeInformationModal from "createProposal/components/TallyFeeInformationModal"
import type { AssetForSelector } from "createProposal/components/actions/AssetSelectorWithInput"
import { isSameAddress, shortenIfAddress } from "web3/helpers/address"
import CreateProposalFormError from "createProposal/components/CreateProposalFormError"
import UserAvatar from "common/components/UserAvatar"
import type {
  Account,
  RecipientAddressQuery,
  RecipientAddressQueryVariables,
} from "query/graphql"
import { ExecutableCallType, RecipientAddressDocument } from "query/graphql"
import TransferErc20Action from "createProposal/action/TransferErc20Action"
import TransferNativeAssetAction from "createProposal/action/TransferNativeAssetAction"
import { useCreateProposal } from "createProposal/providers/CreateProposalProvider"
import type { CreateProposalFormValues } from "createProposal/types/form"
import { CREATE_PROPOSAL_ERRORS } from "errors/createProposal"
import { ROUTES } from "common/constants/routes"
import { labelNumber, removeTrailingZeros } from "common/helpers/number"
import ExternalLink from "ui/components/icons/ExternalLink"
import { useChainReference } from "web3/hooks/useChainReference"
import { isTokenAddress } from "web3/helpers/etherscan"
import WarningIcon from "ui/components/icons/WarningIcon"
import TransferArbitrumErc20Action from "createProposal/action/TransferArbitrumErc20Action"
import {
  TALLY_FEE,
  getTallyRewardRecipient,
} from "createProposal/helpers/reward"
import AssetSelectorWithInput from "createProposal/components/actions/AssetSelectorWithInput"
import { GrpcErrorType, fetcher } from "common/helpers/fetcher"
import { CircleXMark } from "ui/components/icons/font-awesome/solid/CircleXMark"
import {
  FeatureFlag,
  useOrganization,
} from "organization/providers/OrganizationProvider"

type TransferTokensProps = {
  action: TransferErc20Action | TransferNativeAssetAction
  index: number
}

const TransferTokensRecipe: FC<TransferTokensProps> = ({ action, index }) => {
  return (
    <Stack spacing={4}>
      <Target action={action} actionIndex={index} />
      <Value action={action} actionIndex={index} />
    </Stack>
  )
}

export default TransferTokensRecipe

type TargetProps = {
  action: TransferErc20Action | TransferNativeAssetAction
  actionIndex: number
}

const Target: FC<TargetProps> = ({ action, actionIndex }) => {
  const { recipe } = action

  const { getFieldMeta } = useFormikContext<CreateProposalFormValues>()
  const chainReference = useChainReference()

  const [ethAccount, setEthAccount] = useState<any>(undefined)

  /*
    FOR native asset transfer:
      recipient = `targetAddress`

    For ERC20 transfer:
      recipient = `recipient` value of the `transferFrom` method
      targetAddress = token contract
  */
  const fieldName =
    recipe === ExecutableCallType.Nativetransfer
      ? `actions.${actionIndex}.targetAddress`
      : `actions.${actionIndex}.recipient`

  const { value: recipientAddress } = getFieldMeta<string>(fieldName)
  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()

  const lookupAccount = async () => {
    try {
      const metadata = await fetcher.gql<
        RecipientAddressQuery,
        RecipientAddressQueryVariables
      >({
        query: RecipientAddressDocument,
        variables: {
          address: recipientAddress as string,
        },
        omittedErrors: [GrpcErrorType.NotFound, GrpcErrorType.Internal],
      })

      const address = metadata?.address

      const ethAccount = address?.ethAccount

      setEthAccount(ethAccount)
    } catch (error) {}
  }

  const handleRemoveTarget = () => {
    setEthAccount(undefined)
    setFieldValue(fieldName, "")
  }

  useEffect(() => {
    if (isAddress(recipientAddress as string)) {
      lookupAccount()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientAddress])

  const handleValidate = (value: string) => {
    if (isAddress(value)) return

    const targetAddressValidError =
      CREATE_PROPOSAL_ERRORS.actions.target.invalidAddressError()

    return targetAddressValidError.displayValue
  }
  const [isRecipientAddressAToken, setIsRecipientAddressAToken] =
    useState<boolean>(false)
  useEffect(() => {
    // Reset state at the beginning of the effect
    setIsRecipientAddressAToken(false)

    const checkIfValueIsAToken = async () => {
      if (recipientAddress && chainReference) {
        const isToken = await isTokenAddress(chainReference, recipientAddress)

        setIsRecipientAddressAToken(isToken)
      }
    }

    checkIfValueIsAToken()
  }, [recipientAddress, chainReference])

  return (
    <Field name={fieldName} validate={handleValidate}>
      {({ field, meta }: FieldProps) => {
        const { name, value, onBlur, onChange } = field

        const isInvalid = Boolean(meta.error && meta.touched)
        const isValid = Boolean(!meta.error && meta.touched)

        return (
          <FormControl isInvalid={isInvalid}>
            <FormLabel fontWeight="semibold" mb={1}>
              Transfer to
            </FormLabel>
            {ethAccount ? (
              <Flex
                align="center"
                borderColor="inherit"
                borderRadius="2px"
                borderWidth="1px"
                justifyContent="space-between"
                px={4}
                py={3}
              >
                <HStack>
                  <UserAvatar
                    address={ethAccount.address}
                    size="sm"
                    src={ethAccount.picture}
                  />
                  <Text textStyle="body.bold.lg">
                    {isAddress(ethAccount.name)
                      ? shortenIfAddress(ethAccount.address)
                      : ethAccount.name}
                  </Text>
                  {!isAddress(ethAccount.name) ? (
                    <Text color="#667085" textStyle="body.regular.lg">
                      {`(${shortenIfAddress(ethAccount.address)})`}
                    </Text>
                  ) : null}
                  <Link isExternal href={ROUTES.profile(ethAccount.address)}>
                    <Icon
                      as={ExternalLink}
                      color="#98A2B3"
                      h={2.5}
                      mb={0.5}
                      w={2.5}
                    />
                  </Link>
                </HStack>
                <Icon
                  as={CircleXMark}
                  color="gray.700"
                  cursor="pointer"
                  onClick={handleRemoveTarget}
                />
              </Flex>
            ) : (
              <Input
                borderColor={isValid ? "green.500" : "inherit"}
                name={name}
                placeholder="0x..."
                py={6}
                value={value}
                onBlur={onBlur}
                onChange={onChange}
              />
            )}

            {isRecipientAddressAToken && (
              <FormHelperText>
                <HStack>
                  <Icon as={WarningIcon} h={4} w={4} />
                  <Text color="gray.700">
                    This is a token address and in most cases not recommended.
                    Please make sure this is the intended address.
                  </Text>
                </HStack>
              </FormHelperText>
            )}

            <CreateProposalFormError error={meta.error} />
          </FormControl>
        )
      }}
    </Field>
  )
}

type RewardRecipient = Pick<Account, "address" | "picture" | "name"> & {
  rewardValue: string
}

type ValueProps = {
  action: TransferErc20Action | TransferNativeAssetAction
  actionIndex: number
}

const Value: FC<ValueProps> = React.memo(function Value({
  action,
  actionIndex,
}) {
  const { recipe, rewardsRecipients } = action
  const { governance, governanceChainId, tokenBalances, isArbitrum } =
    useCreateProposal()
  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()
  const [transferRecipeAction, setTransferRecipeAction] = useState<
    TransferErc20Action | TransferNativeAssetAction | undefined
  >(undefined)
  const [assets, setAssets] = useState<AssetForSelector[]>([])

  const [shouldHideDustTokens, { toggle: onToggleHideDustTokens }] =
    useBoolean(true)

  const { isFeatureFlagOn } = useOrganization()

  const tallyFeeInformationModal = useDisclosure()

  const selectedAsset = useMemo(() => {
    if (!tokenBalances) return

    if (recipe === ExecutableCallType.Nativetransfer) {
      return tokenBalances?.nativeAsset
    }

    const seletcedErc20 = tokenBalances?.tokens?.find((token) =>
      isSameAddress(token.tokenInfo.address, action.targetAddress as string),
    )

    return seletcedErc20
  }, [recipe, tokenBalances, action])

  useEffect(() => {
    if (tokenBalances) {
      const { nativeAsset, tokens } = tokenBalances

      if (tokens) {
        const assets = [
          nativeAsset.tokenInfo,
          ...tokens.map((token) => token.tokenInfo),
        ]

        if (assets) {
          setAssets(assets)
        }
      }
    }
  }, [tokenBalances])

  const handleChangeAmount = useCallback(
    (newAmount: string) => {
      const { tokenInfo } = action as TransferErc20Action

      if (!newAmount) {
        setFieldValue(`actions.${actionIndex}.value`, 0)

        return
      }

      setFieldValue(
        `actions.${actionIndex}.value`,
        formatUnits(newAmount, tokenInfo?.decimals ?? 18),
      )
    },
    [actionIndex, action, setFieldValue],
  )

  const TALLY_REWARD_FEE = isFeatureFlagOn(FeatureFlag.ExcludeTallyFee)
    ? undefined
    : TALLY_FEE

  useEffect(() => {
    setFieldValue(`actions.${actionIndex}.tallyFee`, TALLY_REWARD_FEE)
  }, [actionIndex, setFieldValue, TALLY_REWARD_FEE])

  useEffect(() => {
    setFieldValue(`actions.${actionIndex}.tallyFee`, TALLY_REWARD_FEE)

    if (transferRecipeAction) {
      transferRecipeAction.setContributorFee(action?.contributorFee)
      transferRecipeAction.setTallyFee(TALLY_REWARD_FEE)
    }
  }, [
    actionIndex,
    setFieldValue,
    action.recipe,
    transferRecipeAction,
    action?.contributorFee,
    TALLY_REWARD_FEE,
  ])

  const tallyRewardValue = new BigNumber(action.value)
    .multipliedBy(new BigNumber(TALLY_REWARD_FEE || 0))
    .toString()

  const recalculateRecipients = (
    rewardRecipients: RewardRecipient[] | undefined,
  ) => {
    const rewardRecipientsCount = rewardRecipients?.length ?? 0

    if (rewardRecipientsCount === 0 || !rewardRecipients) {
      setFieldValue(`actions.${actionIndex}.rewardsRecipients`, [])

      return
    }

    const individualRewardFee =
      (action?.contributorFee ?? 0) / (rewardRecipientsCount - 1)
    const individualRewardValue = String(
      Math.round(Number(action.value) * individualRewardFee),
    )

    const updatedRecipients = rewardRecipients.map(
      (recipient: RewardRecipient, idx) => {
        // Tally recipient
        if (idx === 0) {
          return {
            ...recipient,
            rewardValue: tallyRewardValue,
          }
        }

        return {
          ...recipient,
          rewardValue: individualRewardValue,
        }
      },
    )

    setFieldValue(`actions.${actionIndex}.rewardsRecipients`, updatedRecipients)
  }

  useEffect(() => {
    recalculateRecipients(rewardsRecipients)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    recalculateRecipients(rewardsRecipients)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action.value])

  useEffect(() => {
    recalculateRecipients(rewardsRecipients)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action?.contributorFee])

  const handleChangeAsset = (newAsset: AssetForSelector) => {
    setFieldValue(`actions.${actionIndex}.selectedAsset`, newAsset)

    // 0th index refers to native asset
    if (tokenBalances?.nativeAsset.tokenInfo.address === newAsset.address) {
      // since there is only one native asset, `action` must be of type TransferErc20Action

      const { recipient, value, contributorFee, tallyFee } =
        action as TransferErc20Action

      // `targetAddress` for a TransferNativeAssetAction should be the recipient of the transfer
      // which is the `recipient` of the TransferErc20Action
      const nextTargetAddress = recipient

      const nextAction = new TransferNativeAssetAction({
        targetAddress: nextTargetAddress,
        value,
        rewardsRecipients,
        contributorFee,
        tallyFee,
        tallyRewardRecipient: getTallyRewardRecipient(governanceChainId),
      })

      setTransferRecipeAction(nextAction)

      setFieldValue(`actions.${actionIndex}`, nextAction)

      return
    }

    if (
      !tokenBalances?.tokens ||
      tokenBalances?.nativeAsset.tokenInfo.address === newAsset.address
    )
      return

    // `targetAddress` for a TransferErc20Action is the token contract
    const nextTargetAddress = newAsset.address

    const nextRecipient =
      action instanceof TransferErc20Action ||
      action instanceof TransferArbitrumErc20Action
        ? action.recipient
        : (action.targetAddress as string) // hack to cast to string, it assumes that it can be of type TallyAction, which is incorrect

    const nextAction = isArbitrum
      ? new TransferArbitrumErc20Action({
          recipient: nextRecipient,
          targetAddress: nextTargetAddress,
          tokenInfo: { ...newAsset, decimals: String(newAsset.decimals) },
          contributorFee: action?.contributorFee,
          value: action.value,
          rewardsRecipients,
          tallyRewardRecipient: getTallyRewardRecipient(governanceChainId),
        })
      : new TransferErc20Action({
          recipient: nextRecipient,
          targetAddress: nextTargetAddress,
          tokenInfo: { ...newAsset, decimals: String(newAsset.decimals) },
          contributorFee: action?.contributorFee,
          value: action.value,
          rewardsRecipients,
          tallyRewardRecipient: getTallyRewardRecipient(governanceChainId),
        })

    setTransferRecipeAction(nextAction)

    setFieldValue(`actions.${actionIndex}`, nextAction)

    setFieldValue(
      `actions.${actionIndex}.tokenInfo.decimals`,
      newAsset.decimals,
    )
  }

  const whitelistTokens = governance?.contracts?.tokens.map((token) =>
    token.address.toLowerCase(),
  )

  const byDustToken = (asset: AssetForSelector & { isDust?: boolean }) => {
    // Always show "Arbitrum Mainnet Ether" even if it is a dust token
    if (asset.name === "Arbitrum Mainnet Ether") {
      return true
    }

    if (shouldHideDustTokens) {
      if (whitelistTokens?.includes(asset.address.toLowerCase())) {
        return true
      }

      return !asset.isDust
    }

    return true
  }

  const defaultAmount = action?.value ? removeTrailingZeros(action.value) : ""

  const tallyFeeAmount =
    action.recipe === ExecutableCallType.Erc20transferarbitrum
      ? new BigNumber(0)
      : new BigNumber(action.value).multipliedBy(
          new BigNumber(action.tallyFee || 0),
        )

  const tallyFeeUSDValue = selectedAsset?.tokenInfo?.price?.rate
    ? labelNumber(
        Number(
          tallyFeeAmount
            .multipliedBy(new BigNumber(selectedAsset.tokenInfo.price.rate))
            .toString(),
        ),
      )
    : "N/A"

  return (
    <Stack spacing={1}>
      <Text fontWeight="semibold">Transfer amount</Text>
      <Stack spacing={4}>
        <Stack>
          <AssetSelectorWithInput
            withBalance
            assets={assets
              .filter(byDustToken)
              .filter((asset: AssetForSelector & { name?: string }) =>
                Boolean(asset?.name),
              )}
            defaultAmount={defaultAmount}
            selectPlaceholder="Buy"
            selectedAsset={{
              ...selectedAsset?.tokenInfo,
              address: selectedAsset?.tokenInfo?.address ?? "",
              quoteRate: selectedAsset?.tokenInfo?.price.rate,
              balance: String(selectedAsset?.tokenInfo.balance24H ?? "0"),
              symbol: selectedAsset?.tokenInfo?.symbol ?? "",
            }}
            onChangeAmount={handleChangeAmount}
            onChangeAsset={handleChangeAsset}
          />
          <Flex justify="space-between" w="full">
            {tallyFeeAmount && TALLY_REWARD_FEE ? (
              <HStack>
                <Text
                  cursor="pointer"
                  textStyle="body.regular.md"
                  onClick={onToggleHideDustTokens}
                >
                  {`Tally Fees: ${labelNumber(Number(tallyFeeAmount))} ${
                    selectedAsset?.tokenInfo?.symbol ?? ""
                  } ($${tallyFeeUSDValue})`}
                </Text>
                <Text
                  cursor="pointer"
                  textDecoration="underline"
                  textStyle="body.regular.md"
                  onClick={tallyFeeInformationModal.onOpen}
                >
                  What is the fee for?
                </Text>
              </HStack>
            ) : (
              <Flex />
            )}

            <Text
              cursor="pointer"
              textStyle="body.regular.md"
              onClick={onToggleHideDustTokens}
            >
              {shouldHideDustTokens ? "Show dust tokens" : "Hide dust tokens"}
            </Text>
          </Flex>
        </Stack>
      </Stack>
      <TallyFeeInformationModal
        isOpen={tallyFeeInformationModal.isOpen}
        onClose={tallyFeeInformationModal.onClose}
      />
    </Stack>
  )
})
